import { createSelector, createSlice } from '@reduxjs/toolkit';

const shiftsSlice = createSlice({
    initialState: {
        filters: [],
        shifts: [],
        totalRecords: 0,
        isLoading: false,
        // Separate state for previous shifts
        previousShifts: [],
        previousShiftsTotalRecords: 0,
        isPreviousShiftsLoading: false,
    },
    name: 'shifts',
    reducers: {
        fetchShifts: state => {
            state.shifts = [];
            state.totalRecords = 0;
            state.isLoading = true;
        },
        shiftsFetched: (state, { payload }) => {
            const { results, totalRecords } = payload;
            state.totalRecords = totalRecords || 0;
            state.shifts = results;
            state.isLoading = false;
        },
        shiftsFetchRequestFailed: state => {
            state.isLoading = false;
        },
        shiftPartialUpdate: (state, { payload }) => {
            const { shiftId, updatedData } = payload;

            // Update in upcoming shifts
            state.shifts = state.shifts.map(shift => {
                return shift._id === shiftId
                    ? {
                          ...shift,
                          ...updatedData,
                      }
                    : shift;
            });

            // Update in previous shifts
            state.previousShifts = state.previousShifts.map(shift => {
                return shift._id === shiftId
                    ? {
                          ...shift,
                          ...updatedData,
                      }
                    : shift;
            });
        },
        shiftContractorStatusUpdate: (state, { payload }) => {
            const { shiftId, contractorId, status } = payload;
            const shiftIndex = state.shifts.findIndex(
                shift => shift._id === shiftId,
            );
            if (shiftIndex === -1) {
                return;
            }

            const shift = state.shifts[shiftIndex];
            const contractorIndex =
                shift.isABroadcastRequest.broadcastedTo.findIndex(
                    contractor => contractor.staffer._id === contractorId,
                );
            if (contractorIndex === -1) {
                return;
            }

            shift.isABroadcastRequest.broadcastedTo[contractorIndex].status =
                status;
            state.shifts[shiftIndex] = { ...shift };
        },
        shiftBroadcastedContractorUpdate: (state, { payload }) => {
            const { shiftId, contractorId, updatedData } = payload;
            const shiftIndex = state.shifts.findIndex(
                shift => shift._id === shiftId,
            );
            if (shiftIndex === -1) {
                return;
            }

            const shift = state.shifts[shiftIndex];
            const contractorIndex =
                shift.isABroadcastRequest.broadcastedTo.findIndex(
                    contractor => contractor.staffer._id === contractorId,
                );
            if (contractorIndex === -1) {
                return;
            }

            shift.isABroadcastRequest.broadcastedTo[contractorIndex] = {
                ...shift.isABroadcastRequest.broadcastedTo[contractorIndex],
                ...updatedData,
            };
            // Update the shift in the state
            state.shifts[shiftIndex] = { ...shift };
        },
        modifyShiftFilters: (state, { payload }) => {
            const { data, remove = false } = payload;

            data.forEach(filter => {
                const { key, value } = filter;
                const index = state.filters.findIndex(f => f.key === key);
                // If no filter exist and value is also nothing then return
                if (index === -1 && value === '') {
                    return;
                }

                if (remove) {
                    if (index !== -1) {
                        state.filters.splice(index, 1);
                    }
                    return;
                }

                if (index === -1) {
                    state.filters.push(filter);
                } else {
                    state.filters[index].value = value;
                }
            });
        },
        removeShiftFilter: (state, { payload }) => {
            const index = state.filters.findIndex(f => f.key === payload);

            if (index !== -1) {
                state.filters.splice(index, 1);
            }
        },
        removeAllShiftFilters: state => {
            state.filters = [];
            return;
        },
        createShift: state => {
            state.isLoading = true;
        },
        updateShift: state => {
            state.isLoading = true;
        },
        shiftCreated: state => {
            state.isLoading = false;
        },
        shiftUpdated: state => {
            state.isLoading = false;
        },
        shiftCreationFailed: state => {
            state.isLoading = false;
        },
        deleteShift: (state, { payload }) => {
            const index = state.shifts.findIndex(
                shift => shift._id === payload,
            );
            if (index !== -1) {
                state.shifts.splice(index, 1);
            }
        },
        shiftUpdateFailed: state => {
            state.isLoading = false;
        },
        fetchPreviousShifts: state => {
            state.previousShifts = [];
            state.previousShiftsTotalRecords = 0;
            state.isPreviousShiftsLoading = true;
        },
        previousShiftsFetched: (state, { payload }) => {
            const { results, totalRecords } = payload;
            state.previousShiftsTotalRecords = totalRecords || 0;
            state.previousShifts = results;
            state.isPreviousShiftsLoading = false;
        },
        previousShiftsFetchRequestFailed: state => {
            state.isPreviousShiftsLoading = false;
        },
        startEditingInvoice: (state, { payload }) => {
            // Update in previous shifts only (tracker editing is only for previous shifts)
            const previousShiftIndex = state.previousShifts.findIndex(
                shift => shift._id === payload.shiftId,
            );
            if (previousShiftIndex !== -1) {
                state.previousShifts[
                    previousShiftIndex
                ].startEditingInvoice = true;
            }
        },
        endEditing: (state, { payload }) => {
            // Update in previous shifts only (tracker editing is only for previous shifts)
            const previousShiftIndex = state.previousShifts.findIndex(
                shift => shift._id === payload.shiftId,
            );
            if (previousShiftIndex !== -1) {
                state.previousShifts[
                    previousShiftIndex
                ].startEditingInvoice = false;
            }
        },
        finalizeBilling: state => {
            state.isLoading = true;
        },
        billingFinalized: state => {
            state.isLoading = false;
        },
        billingFinalizeFailed: state => {
            state.isLoading = false;
        },
        // Tracker editing actions
        createTrackerRequest: state => {
            state.isLoading = true;
        },
        updateTrackerRequest: state => {
            state.isLoading = true;
        },
        createTrackerSuccess: state => {
            state.isLoading = false;
        },
        updateTrackerSuccess: state => {
            state.isLoading = false;
        },
        createTrackerFailed: state => {
            state.isLoading = false;
        },
        updateTrackerFailed: state => {
            state.isLoading = false;
        },
        contractorTrackedTimeUpdated: (state, { payload }) => {
            const { shiftId, contractorId, trackedTime, tracker, invoice } =
                payload;

            const updateCollection = collection =>
                collection.map(shift => {
                    if (!shift || shift._id !== shiftId) {
                        return shift;
                    }

                    if (!Array.isArray(shift.staffersAndShifts)) {
                        return shift;
                    }

                    const updatedStaffers = shift.staffersAndShifts.map(
                        stafferAndShift => {
                            // stafferAndShift.staffer is always an object (populated from backend)
                            const currentStafferId =
                                stafferAndShift.staffer._id;

                            if (currentStafferId !== contractorId) {
                                return stafferAndShift;
                            }

                            // Update invoice in staffersAndShifts for non-broadcast requests
                            const updatedInvoices =
                                invoice !== undefined
                                    ? [invoice]
                                    : stafferAndShift.invoices || [];

                            return {
                                ...stafferAndShift,
                                trackedTime:
                                    trackedTime !== undefined
                                        ? trackedTime
                                        : stafferAndShift.trackedTime,
                                tracker:
                                    tracker !== undefined
                                        ? tracker
                                        : stafferAndShift.tracker,
                                invoices: updatedInvoices,
                            };
                        },
                    );

                    return {
                        ...shift,
                        staffersAndShifts: updatedStaffers,
                        invoice:
                            invoice !== undefined ? invoice : shift.invoice,
                    };
                });

            // Only update previous shifts (tracker editing is only for previous shifts)
            state.previousShifts = updateCollection(state.previousShifts);
        },
    },
});

// selectors
const getSelf = state => state.shifts;

export const getShifts = createSelector(getSelf, state => state.shifts);

export const isLoading = createSelector(getSelf, state => state.isLoading);

export const getTotalRecords = createSelector(
    getSelf,
    state => state.totalRecords,
);

export const getShiftFilters = createSelector(getSelf, state => state.filters);

// Previous shifts selectors
export const getPreviousShifts = createSelector(
    getSelf,
    state => state.previousShifts,
);

export const getPreviousShiftsTotalRecords = createSelector(
    getSelf,
    state => state.previousShiftsTotalRecords,
);

export const isPreviousShiftsLoading = createSelector(
    getSelf,
    state => state.isPreviousShiftsLoading,
);

export const {
    fetchShifts,
    shiftsFetched,
    shiftPartialUpdate,
    shiftContractorStatusUpdate,
    shiftBroadcastedContractorUpdate,
    shiftsFetchRequestFailed,
    modifyShiftFilters,
    removeShiftFilter,
    removeAllShiftFilters,
    createShift,
    updateShift,
    shiftCreated,
    shiftUpdated,
    shiftCreationFailed,
    deleteShift,
    shiftUpdateFailed,
    fetchPreviousShifts,
    previousShiftsFetched,
    previousShiftsFetchRequestFailed,
    startEditingInvoice,
    endEditing,
    finalizeBilling,
    billingFinalized,
    billingFinalizeFailed,
    createTrackerRequest,
    updateTrackerRequest,
    createTrackerSuccess,
    updateTrackerSuccess,
    createTrackerFailed,
    updateTrackerFailed,
    contractorTrackedTimeUpdated,
} = shiftsSlice.actions;

export default shiftsSlice.reducer;
